/**
 * Player-facing options, mirroring `docs/settings-mockup.png`:
 * music, sound effects, player voices, and a cheat/unlock code field.
 */
export interface GameSettings {
  music: boolean;
  soundEffects: boolean;
  playerVoices: boolean;
  /** Normalised unlock code, or null when none has been entered. */
  code: string | null;
}

export const DEFAULT_SETTINGS: GameSettings = {
  music: true,
  soundEffects: true,
  playerVoices: true,
  code: null,
};

const STORAGE_KEY = 'koodooracers.settings.v2';

export function normaliseCode(raw: string): string | null {
  const trimmed = raw.trim().toUpperCase();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Merges stored settings over the defaults, discarding anything malformed.
 * Never throws — a corrupt or unavailable localStorage falls back to defaults
 * so the game always boots.
 */
export function parseSettings(raw: string | null): GameSettings {
  if (!raw) {
    return { ...DEFAULT_SETTINGS };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_SETTINGS };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return { ...DEFAULT_SETTINGS };
  }

  const candidate = parsed as Partial<Record<keyof GameSettings, unknown>>;
  const bool = (value: unknown, fallback: boolean): boolean =>
    typeof value === 'boolean' ? value : fallback;

  return {
    music: bool(candidate.music, DEFAULT_SETTINGS.music),
    soundEffects: bool(candidate.soundEffects, DEFAULT_SETTINGS.soundEffects),
    playerVoices: bool(candidate.playerVoices, DEFAULT_SETTINGS.playerVoices),
    code: typeof candidate.code === 'string' ? normaliseCode(candidate.code) : null,
  };
}

export function loadSettings(storage: Storage | undefined = safeStorage()): GameSettings {
  if (!storage) {
    return { ...DEFAULT_SETTINGS };
  }
  try {
    return parseSettings(storage.getItem(STORAGE_KEY));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(
  settings: GameSettings,
  storage: Storage | undefined = safeStorage(),
): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Private browsing or a full quota — settings simply do not persist.
  }
}

/**
 * localStorage access throws outright in some embedded/privacy contexts, and
 * Node ships its own unusable `localStorage` global that shadows the DOM one,
 * so read it off `window` and treat any failure as "no storage".
 */
function safeStorage(): Storage | undefined {
  try {
    return globalThis.window?.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}
